// COP Copilot — the narrow panel-facing API (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Mirrors chatCapability.ts ChatApi: the SMALL surface the dockable panel consumes, decoupled
// from HostContext so the panel depends ONLY on this interface — never on the bus, the engine,
// the PDP, or the capability internals. CopilotPanel.tsx imports this type alone.
//
// WAVE-A SCOPE (legacy default): ask() returns a CANNED/stubbed analyst turn that summarizes the
// copilot's working set when NO runtime runner is supplied — there is no model call on that path.
//
// WAVE-B WIRING (the real analyst loop): when a `runner` is supplied (createCopilotApi opts), ask()
// drives the LIFTED takbot agent-runtime (copilot/runtime/*): the clearance-aware system prompt +
// the user query + the copilot's MCP tools executing the think⇄instant tool loop OVER THE HOST-
// FILTERED (PDP-visible) working set ONLY. The runner returns the synthesized, cited answer + the
// classification high-water-mark roll-up; ask() renders it as a real copilot turn (text +
// citations + result preview + HWM note). The runner is wired in copilotCapability.ts; the model
// backend (local Qwen3.6 via the OpenAI /v1 path) falls back to the deterministic MockBackend when
// no local server is reachable, so this surface NEVER hard-depends on a running model.
//
// Holds NO React. Imports nothing from HostContext.

/** Who authored a turn — the human operator or the copilot. */
export type CopilotRole = "user" | "copilot";

/** A citation the synthesized answer grounds a claim in (the result_ref / row id + the inline
 *  [n] index). Surfaced by the lifted runtime's FinalAnswer; never carries a denied object. */
export interface CopilotCitation {
  ref: string;
  index: number;
  label?: string;
}

/** One ranked-list entry inside a copilot turn (the mock's `msg.list` row — the "top N"
 *  pattern). ADDITIVE + BACKWARD-COMPATIBLE: turns without a `list` are unaffected. The
 *  runtime may populate this when it returns structured ranked data; when it doesn't, the
 *  panel simply renders no list (we NEVER fabricate ranked rows). Glomar-safe by construction:
 *  a denied object is never in the working set the copilot reasons over, so it can never land
 *  here. `affiliation` drives the per-row dot hue via the shell's --status-* tokens (Rule 2 —
 *  hue = affiliation only); omit it to fall back to the unknown hue. `ref` is the cited entity
 *  id (the CITE chip target) — inert today (no map-select seam from the panel). */
export interface CopilotListItem {
  /** 1-based rank (the red-tinted rank badge). */
  rank: number;
  /** The primary call/label (Saira 600). */
  call: string;
  /** The secondary meta line (mono). */
  meta: string;
  /** APP-6 affiliation that colours the row dot (hostile/friendly/neutral/unknown/pending). */
  affiliation?: "hostile" | "friendly" | "neutral" | "unknown" | "pending";
  /** The CITE-chip caption (default "CITE ↗" rendered when `ref` is present). */
  cta?: string;
  /** Cited entity id — the CITE-chip target. Inert today (no panel→map select seam). */
  ref?: string;
}

/** One action the copilot offers under a turn (the mock's `msg.actions` teal pills). ADDITIVE +
 *  BACKWARD-COMPATIBLE. `act` runs client-side when the operator clicks the pill. */
export interface CopilotAction {
  label: string;
  act: () => void;
}

/** One turn in the copilot conversation (the panel's view). */
export interface CopilotTurn {
  /** Stable id (used for de-dup / React keys). */
  id: string;
  role: CopilotRole;
  /** The displayed text of the turn. */
  text: string;
  /** ISO timestamp; the ordering key. */
  time: string;
  /** Optional structured result the panel may render (e.g. a Q1 result preview). NEVER carries
   *  a denied object — the working set the copilot reasons over is the visible-only set. */
  result?: { columns: string[]; rows: Array<Record<string, unknown>>; rows_returned: number };
  /** Optional Glomar-safe notice (e.g. "0 results" reads identically for empty and denied). */
  note?: string;
  /** The grounded citations the synthesized answer carries (lifted-runtime path). */
  citations?: CopilotCitation[];
  /** The classification high-water-mark roll-up the answer inherits (TDF banner). */
  marking?: string;
  /** The honest coverage caveat (LIMITED framing) the runtime surfaced. */
  coverage?: string;
  /** Optional ranked-list rows (the mock's "top N" pattern). ADDITIVE; absent on plain turns. */
  list?: CopilotListItem[];
  /** Optional action pills under the turn. ADDITIVE; absent on plain turns. */
  actions?: CopilotAction[];
}

/** What the lifted runtime returns for one ask() turn — the synthesized answer the panel renders.
 *  The capability's `runner` (copilotCapability.ts) produces this by driving the agent loop over
 *  the confined working set. A denied object is NEVER present (Glomar by construction). */
export interface CopilotRunResult {
  text: string;
  citations?: CopilotCitation[];
  marking?: string;
  coverage?: string;
  result?: { columns: string[]; rows: Array<Record<string, unknown>>; rows_returned: number };
  note?: string;
  /** Optional structured ranked rows the runtime synthesized (mock "top N" pattern). ADDITIVE. */
  list?: CopilotListItem[];
  /** Optional action pills the runtime offers under the answer. ADDITIVE. */
  actions?: CopilotAction[];
}

/** The runtime runner: drive the lifted agent loop for one user query → a synthesized turn. */
export type CopilotRunner = (text: string) => Promise<CopilotRunResult>;

/** The narrow API the CopilotPanel consumes. Decoupled from the capability + HostContext. */
export interface CopilotApi {
  /** The acting subject's analyst-session label (display only). */
  readonly persona: string;
  /** A SHORT acting-subject tag for the header ("AI-AGENT · ACTING AS {personaShort}"). ADDITIVE
   *  (optional): when absent the panel falls back to `persona`. Display only — NOT a security
   *  boundary (the PDP gates the working set; this is a label). NOTE: this is a SNAPSHOT getter,
   *  not a reactive value — the panel reads it inside its subscribe callback so a relabel (via
   *  setPersonaShort) repaints the header on the next conversation emit. */
  readonly personaShort?: string;
  /** RELABEL the acting-subject tag (C4) — the host calls this on a persona switch (setIdentity)
   *  so the header re-reads "ACTING AS {personaShort}" for the new operator. Display only — NOT a
   *  security boundary (the host-filtered bus is what re-scopes the working set). Notifies
   *  subscribers so the panel repaints without a new ask. ADDITIVE; the runtime path is untouched. */
  setPersonaShort(next: string): void;
  /** Current ordered snapshot (oldest → newest) of the conversation. */
  results(): CopilotTurn[];
  /** Ask the copilot something. Appends a user turn + a copilot turn, returns the copilot turn.
   *  When a `runner` is wired, this drives the lifted agent loop over the confined working set and
   *  renders the synthesized, cited answer; otherwise it falls back to the canned summary. */
  ask(text: string): Promise<CopilotTurn>;
  /** EJECT the agent: CLEAR the conversation thread (turns[]) AND the runtime DRIVER STATE
   *  beyond it (the visible working set + any cached backend/run cursor) when the capability
   *  supplied an `onEject` hook (createCopilotApi opts). This is the SECURITY-state wipe used on
   *  a classification DOWNGRADE: after eject() no higher-cls reasoning OR higher-cls working-set
   *  row is retained under the lower view. ADDITIVE — the ask()/subscribe() path is untouched.
   *  After eject() the snapshot is empty and subscribers are notified; the panel pairs this with
   *  closing the dock. Idempotent. */
  eject(): void;
  /** EPHEMERAL RESET — the FULL host-driven purge: clear the conversation thread (like eject) AND
   *  the copilot's read-side working set (via the capability-supplied `clearWorkingSet` hook). The
   *  COP host calls this on a persona switch, a query-level DOWNGRADE, and logout/teardown so NO
   *  higher-classification Q&A turn or row survives into a lower view (mirrors the chat-store
   *  rebuild at those same points). When no `clearWorkingSet` hook is wired this degrades to an
   *  eject (turns only). Subscribers are notified; the next ask() starts a fresh thread over the
   *  re-ingested (cleared) census. */
  reset(): void;
  /** Subscribe to conversation changes. Fires immediately with the current snapshot, then on
   *  every ask. Returns an unsubscribe. */
  subscribe(cb: (turns: CopilotTurn[]) => void): () => void;
}

/** A read-only view of the copilot's visible working set, supplied by the capability so the
 *  canned analyst can describe what the persona can see WITHOUT the api touching HostContext. */
export interface WorkingSetView {
  /** Count of visible rows the copilot may reason over (denied objects are absent). */
  size(): number;
  /** The visible-only manifest catalog (already Glomar-safe — see copilotTables.ts). */
  manifest(): Record<string, unknown>;
}

/** Construction options for the copilot API. */
export interface CopilotApiOptions {
  /** Display label for the analyst session (default "COP Copilot"). */
  persona?: string;
  /** Short acting-subject tag for the header ("ACTING AS {personaShort}"). Display only. */
  personaShort?: string;
  /** The visible working-set view the canned analyst summarizes (used only when no runner). */
  workingSet?: WorkingSetView;
  /** The lifted-runtime runner. When supplied, ask() drives the real agent loop over the confined
   *  working set; when absent, ask() falls back to the canned working-set summary. */
  runner?: CopilotRunner;
  /** EPHEMERAL-RESET hook: the capability supplies this to empty the working set when the host
   *  calls reset(). Display surface owns nothing — this is the seam by which reset() reaches the
   *  capability's CopilotWorkingSet.clear() without the api importing the capability internals.
   *  Lifecycle purge (persona switch, logout); pairs with onEject for the downgrade security wipe. */
  clearWorkingSet?: () => void;
  /** Optional RUNTIME-STATE wipe hook, invoked by eject() AFTER the thread is cleared. The
   *  capability supplies this to clear the DRIVER STATE eject() cannot reach from here — the
   *  visible WORKING SET (CopilotWorkingSet) and any cached backend/run cursor — so a
   *  classification downgrade leaves NO higher-cls reasoning OR higher-cls working-set row behind.
   *  ABSENT ⇒ eject() clears only turns[] (legacy behavior; back-compat). A throwing hook is
   *  swallowed (the thread clear always stands). */
  onEject?: () => void;
  /** Optional seed turns so the panel looks alive on first paint. */
  seed?: Array<Pick<CopilotTurn, "role" | "text"> & Partial<CopilotTurn>>;
}

let turnSeq = 0;
function nextId(): string {
  turnSeq += 1;
  return `copilot:turn:${turnSeq}:${Date.now()}`;
}

/** Build the canned analyst summary — the FALLBACK when no runtime runner is wired (e.g. the
 *  panel is constructed bare). It is deliberately HONEST and never invents data beyond the visible
 *  working set — a "0 objects" answer reads identically whether the set is genuinely empty or an
 *  object was denied (Glomar). */
function cannedAnswer(text: string, ws: WorkingSetView | undefined): CopilotTurn {
  const size = ws?.size() ?? 0;
  const catalog = ws?.manifest() ?? {};
  const affiliations = ((catalog.entity_values as { affiliation?: string[] } | undefined)?.affiliation ?? []).join(", ");
  const body =
    `I can see ${size} object(s) you are cleared for. ` +
    (affiliations ? `Visible affiliations: ${affiliations}. ` : "") +
    `Ask me to run a structured geo-query (bbox / radius / within a zone) over this working set, ` +
    `or request EVAC (which is human-gated — I can surface the intent but cannot dispatch it).`;
  return { id: nextId(), role: "copilot", text: body, time: new Date().toISOString(), note: `re: ${text.trim().slice(0, 80)}` };
}

/** Render a lifted-runtime CopilotRunResult into a copilot turn (text + citations + result + HWM). */
function turnFromRunResult(r: CopilotRunResult): CopilotTurn {
  return {
    id: nextId(),
    role: "copilot",
    text: r.text,
    time: new Date().toISOString(),
    ...(r.result ? { result: r.result } : {}),
    ...(r.note ? { note: r.note } : {}),
    ...(r.citations && r.citations.length > 0 ? { citations: r.citations } : {}),
    ...(r.marking ? { marking: r.marking } : {}),
    ...(r.coverage ? { coverage: r.coverage } : {}),
    ...(r.list && r.list.length > 0 ? { list: r.list } : {}),
    ...(r.actions && r.actions.length > 0 ? { actions: r.actions } : {}),
  };
}

/** Create the panel-facing copilot API. The capability owns the WorkingSetView; the panel owns
 *  nothing but this surface. */
export function createCopilotApi(opts: CopilotApiOptions = {}): CopilotApi {
  const persona = opts.persona ?? "COP Copilot";
  // MUTABLE so C4 (setPersonaShort) can relabel the header on a persona switch. The panel reads
  // it through the getter below inside its subscribe callback, so a relabel + emit() repaints.
  let personaShort = opts.personaShort;
  const runner = opts.runner;
  const onEject = opts.onEject;
  const turns: CopilotTurn[] = [];
  const listeners = new Set<(turns: CopilotTurn[]) => void>();

  const snapshot = (): CopilotTurn[] => [...turns];
  const emit = (): void => {
    const snap = snapshot();
    for (const cb of listeners) {
      try {
        cb(snap);
      } catch {
        // a bad listener must not break the conversation
      }
    }
  };

  for (const s of opts.seed ?? []) {
    turns.push({
      id: s.id ?? nextId(),
      role: s.role ?? "copilot",
      text: s.text,
      time: s.time ?? new Date().toISOString(),
      ...(s.result ? { result: s.result } : {}),
      ...(s.note ? { note: s.note } : {}),
    });
  }

  return {
    persona,
    // A GETTER (not a captured value) so setPersonaShort's relabel is visible to every read.
    get personaShort(): string | undefined {
      return personaShort;
    },
    setPersonaShort(next: string): void {
      const trimmed = next.trim();
      if (!trimmed || trimmed === personaShort) return;
      personaShort = trimmed;
      // Repaint the panel (it re-reads personaShort in its subscribe callback) without a new ask.
      emit();
    },
    results: () => snapshot(),
    eject(): void {
      // CLEAR the thread (not merely hide): an ejected agent leaves no conversation behind.
      turns.length = 0;
      // SECURITY-state wipe: also clear the runtime DRIVER STATE eject() cannot reach from here
      // (the visible WORKING SET + cached backend/run cursor) when the capability supplied the
      // hook. This is what makes eject() a TRUE downgrade wipe — no higher-cls working-set row
      // survives under a lower view. Fail-soft: a throwing hook never blocks the thread clear.
      if (onEject) {
        try {
          onEject();
        } catch {
          // the thread clear always stands; a bad runtime-wipe hook must not break eject.
        }
      }
      emit();
    },
    reset(): void {
      // FULL ephemeral purge: drop the thread AND the working set (the capability-supplied hook).
      // After this NO higher-classification turn or row survives — the host calls it on persona
      // switch / query-level downgrade / logout. clearWorkingSet is best-effort (a bad hook must
      // not break the conversation reset). One emit() repaints the now-empty panel.
      turns.length = 0;
      try {
        opts.clearWorkingSet?.();
      } catch {
        // the working-set clear must not throw the thread reset
      }
      emit();
    },
    async ask(text: string): Promise<CopilotTurn> {
      const trimmed = text.trim();
      const userTurn: CopilotTurn = { id: nextId(), role: "user", text: trimmed, time: new Date().toISOString() };
      turns.push(userTurn);
      emit(); // paint the user turn immediately; the answer streams in below

      let reply: CopilotTurn;
      if (runner) {
        try {
          reply = turnFromRunResult(await runner(trimmed));
        } catch (e) {
          // The lifted runtime ALWAYS terminates with a final, so a thrown error here is an
          // unexpected runner failure — surface it honestly + LIMITED, never a fabricated answer.
          const msg = e instanceof Error ? e.message : String(e);
          reply = {
            id: nextId(),
            role: "copilot",
            text: `The copilot runtime failed to answer (${msg}). This answer is LIMITED — treat it as a non-answer, not a definitive negative.`,
            time: new Date().toISOString(),
            note: `re: ${trimmed.slice(0, 80)}`,
          };
        }
      } else {
        reply = cannedAnswer(trimmed, opts.workingSet);
      }
      turns.push(reply);
      emit();
      return reply;
    },
    subscribe(cb) {
      listeners.add(cb);
      cb(snapshot());
      return () => {
        listeners.delete(cb);
      };
    },
  };
}
